import { Fragment, useEffect, useMemo, useState } from "react";
import { api, Manifest } from "../api";
import { fmtTime } from "../format";
import { pct, px, signedUsd, usd } from "../lib/money";
import TradeStory from "./TradeStory";
import SleevePerformance from "./SleevePerformance";
import CapitalAtWork from "./CapitalAtWork";
import { Money, MoneySwitch, ScopeNote } from "./MoneyScope";
import RightAndWrong from "./RightAndWrong";
import OlderToggle from "./OlderToggle";
import Ticker from "./Ticker";
import { RECENT_BUSINESS_DAYS, splitRecent } from "../lib/recent";

type RealReturn = {
  nominal_return_pct: number | null; capital_deployed: number; period_days: number;
  assumed_inflation_pct: number | null; inflation_over_period_pct: number | null;
  real_return_pct: number | null; note: string;
};
type Stats = {
  trades: number; wins: number; losses: number; scratches?: number; win_rate: number;
  net_pnl: number; gross_profit: number; gross_loss: number;
  profit_factor: number | null; avg_win: number; avg_loss: number;
  expectancy: number; largest_win: number; largest_loss: number; max_drawdown: number;
  // G-Pop's honest-numbers pair (2026-08-06): the arithmetic average return
  // every report leads with, vs. the geometric mean — what compounding these
  // trades actually implies — plus nominal-vs-inflation-adjusted return on
  // capital deployed. "No lipstick on a pig" — Alan.
  arithmetic_avg_pnl_pct: number | null; geometric_avg_pnl_pct: number | null; return_gap_pct: number | null;
  real_return: RealReturn | null;
};
export type Trade = {
  book: string; symbol: string; qty: number; entry_px: number; exit_px: number;
  entry_time: string | null; exit_time: string | null; pnl: number; pnl_pct: number;
  hold_min: number | null; win: boolean; result?: string; junior: string | null;
  side: string;
  directives: number[]; exit_order_id: string;
  // Unique per closed LOT. exit_order_id is not: one exit order can close
  // several FIFO-matched lots, so keying anything on it collapses visibly
  // different rows onto one record (found 2026-08-04).
  trade_key: string;
  // Added 2026-08-04 (design + CFO review): exit_reason distinguishes "hit
  // its own stop/target/time-exit" from an ordinary loss so a repeat of the
  // stale-proposal false-stop-out bug would be visible in the list itself,
  // not just after opening Story. fx_pair/fx_unconverted make currency
  // conversion visible instead of a bare "$" figure — fx_unconverted flags
  // the one still-live gap (a cross pair with no direct USD leg).
  exit_reason: string | null; fx_pair: boolean; fx_unconverted: boolean;
};
type ReconStatus = { ok: boolean | null; critical_count: number | null; error?: string };
type TradeDetail = {
  found: boolean; trade: Trade; path: { t: string; c: number }[];
  if_held: { label: string; price: number; would_pnl: number; vs_actual: number; final?: boolean }[];
  best_exit: { price: number; t: string; would_pnl: number; left_on_table: number } | null;
  signal_freshness: Record<string, { at: string; min_before_entry: number }>;
};
type Curve = { t: string | null; symbol: string; pnl: number; cum: number };
type Attr = { k: string; pnl: number };
type OpenPos = { book: string; symbol: string; qty: number; entry_px: number; entry_time: string | null };
type TotalValue = {
  available: boolean; reason?: string; period: string; as_of: string;
  total_value: number; start_value: number; change: number; funding: number;
  net_change: number; net_change_pct: number | null; twr_pct: number;
  days: number; partial_days: number; venues: Record<string, number>;
  points: { date: string; value: number; flow: number; complete: boolean }[]; note: string;
};
type Bridge = {
  day_change: number; realized_today: number; unrealized_move: number;
  day_funding: number; note: string;
};
type Summary = {
  bridge: Bridge | null;
  stats: Stats; equity_curve: Curve[]; trades: Trade[];
  by_junior: Attr[]; by_symbol: Attr[]; open_positions: OpenPos[];
  is_paper: boolean; mixes_money: boolean; scope_books: string[];
};

// The REAL book list — the growth books were missing, leaving their trades
// visible only unattributed inside "all". Shared with Ledger.tsx (the parent
// that now owns book selection — see BOOKS/BOOK_LABEL export note there):
// this page's filter used to be local-only, so switching it had no effect on
// the sibling Orders/Post-Mortems tabs, which just silently followed the
// sidebar's book instead (found 2026-08-04, Alan: "cluttered with paper
// trans and the filter doesn't seem to deal with it").
export const BOOKS = ["all", "paper", "live", "paper_long_term", "live_long_term"] as const;
export const BOOK_LABEL: Record<string, string> = {
  all: "all", paper: "Paper·Daily", live: "Live·Daily",
  paper_long_term: "Paper·Growth", live_long_term: "Live·Growth",
};
// usd/px moved to lib/money.ts — ONE formatter for the whole app (Kaizen
// 2026-08 item 12). Re-exported here because TradeStory and Ledger already
// import from this module.
export { px, usd };

export default function Performance({ book }: { manifest: Manifest; book: string }) {
  const [data, setData] = useState<Summary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TradeDetail | null>(null);
  const [storyId, setStoryId] = useState<string | null>(null);
  const [recon, setRecon] = useState<ReconStatus | null>(null);
  // Defaults to all-time since inception, not "today" — that WAS the whole
  // confusion (Alan, 2026-08-06: "whats the p&l time duration? i thought it
  // was daily?"). "Live · Daily" names the book's TRADING STYLE (intraday,
  // flattened by close), not a P&L window; this toggle is the actual window.
  const [period, setPeriod] = useState<"all" | "today">("all");
  // Real money by default when looking at every book — the headline used to
  // blend a paper rehearsal into it (−$724.67, of which −$722 was simulated).
  const [money, setMoney] = useState<Money>("real");
  const [tv, setTv] = useState<TotalValue | null>(null);
  // The list is collapsed to the current week by default; everything older is
  // one click away, never dropped. Purely a display cut — the stats above are
  // still computed over the whole period the toggle selects.
  const [showOlder, setShowOlder] = useState(false);

  useEffect(() => {
    let live = true;
    api.get<ReconStatus>("/api/performance/reconciliation-status")
      .then((r) => { if (live) setRecon(r); }).catch(() => {});
    api.get<TotalValue>("/api/performance/total-value?period=all")
      .then((r) => { if (live) setTv(r); }).catch(() => {});
    return () => { live = false; };
  }, []);

  async function toggleTrade(exitId: string) {
    if (openId === exitId) { setOpenId(null); setDetail(null); return; }
    setOpenId(exitId); setDetail(null);
    try {
      const d = await api.get<TradeDetail>(`/api/performance/trade?exit_order_id=${exitId}`);
      if (d.found) setDetail(d);
    } catch { /* leave null */ }
  }

  useEffect(() => {
    let live = true;
    const fetchSummary = async () => {
      try {
        const q = book === "all" ? `&money=${money}` : "";
        const d = await api.get<Summary>(`/api/performance/summary?book=${book}&period=${period}${q}`);
        if (live) { setData(d); setErr(null); }
      } catch (e) { if (live) setErr((e as Error).message); }
    };
    void fetchSummary();
    // Poll — the page used to fetch once, so a fill landing while you watched
    // never appeared until you re-navigated (Kaizen D5/F7).
    const iv = setInterval(fetchSummary, 15000);
    return () => { live = false; clearInterval(iv); };
  }, [book, period, money]);

  const s = data?.stats;
  const net = s?.net_pnl ?? 0;

  // The closed-trade list, split for display only: this week's rows always
  // render, older ones sit behind the toggle. The three call sites below used
  // to re-filter the same array inline; one memo keeps the mobile cards, the
  // desktop table and the empty-state from ever disagreeing about the count.
  const shown = useMemo(() => {
    const mine = (data?.trades ?? []).filter((t) => book === "all" || t.book === book);
    const { recent, older } = splitRecent(mine, (t) => t.exit_time);
    return { all: mine, recent, older };
  }, [data, book]);

  // "all" blends every book's real/paper money into one headline — CFO
  // review flagged that as a real misread risk with no visibility into the
  // components. Computed client-side from trades already in `data`, no
  // extra request.
  const bookBreakdown = useMemo(() => {
    if (book !== "all" || !data) return null;
    const by: Record<string, number> = {};
    for (const t of data.trades) by[t.book] = (by[t.book] ?? 0) + t.pnl;
    return by;
  }, [book, data]);

  // Board-directed (a human-reviewable board thesis exists) vs mechanical
  // (crypto/forex sleeve, a standing rule fired, no board seat involved) —
  // fundamentally different risk profiles that were previously commingled
  // into one set of bars. junior is only ever set when a Proposal exists.
  const executionSplit = useMemo((): Attr[] => {
    if (!data) return [];
    let boardPnl = 0, mechPnl = 0, boardN = 0, mechN = 0;
    for (const t of data.trades) {
      if (t.junior) { boardPnl += t.pnl; boardN++; } else { mechPnl += t.pnl; mechN++; }
    }
    return [
      { k: `Board-directed (${boardN})`, pnl: boardPnl },
      { k: `Mechanical (${mechN})`, pnl: mechPnl },
    ];
  }, [data]);

  return (
    <div className="space-y-4">
      <RightAndWrong />
      <div className="flex items-center gap-2 flex-wrap">
        <ReconBadge recon={recon} />
        <div className="flex text-xs rounded-md overflow-hidden border border-edge ml-auto">
          {(["all", "today"] as const).map((p) => (
            <button key={p} onClick={() => setPeriod(p)}
                    className={`px-2.5 py-1 font-sans ${period === p ? "bg-accent/20 text-accent font-semibold" : "text-slate-400 hover:text-slate-200"}`}>
              {p === "all" ? "All-time" : "Today"}
            </button>
          ))}
        </div>
      </div>
      {err && <div className="text-down text-sm">{err}</div>}
      {book === "all" && (
        <div className="flex justify-end"><MoneySwitch book={book} money={money} onChange={setMoney} /></div>
      )}

      {/* THE headline: what the firm is worth. Alan, 2026-08-20 — "every
          brokerage ive ever seen shows the total current value of all the
          assets at that time", and he is right: realized-only measured exit
          timing and rewarded holding losers. Realized is a component below. */}
      {tv?.available && money === "real" && book === "all" && <TotalValueCard tv={tv} />}

      {/* Realized detail: the one number + the two that qualify it, then the curve.
          Previously buried in an 8-tile uniform grid — design review: a
          reader couldn't find "are we winning" in 5 seconds. */}
      <div className="card">
        <div className="flex items-end gap-6 flex-wrap">
          <div>
            <span className="label mb-0">Realized P&amp;L <span className="normal-case text-slate-500">— closed trades only, {period === "today" ? "today" : "all-time"}{tv?.available ? " · a component of total value above" : ""}</span></span>
            {data && <ScopeNote book={book} money={money} isPaper={data.is_paper} books={data.scope_books} />}
            <div className={`text-4xl font-black font-mono leading-none mt-1 ${net >= 0 ? "text-up" : "text-down"}`}>
              {usd(net)}
            </div>
            <span className="text-xs text-slate-500">{s?.trades ?? 0} closed trades</span>
          </div>
          <div className="flex gap-6">
            <div>
              <div className="text-xs text-slate-500">Win rate</div>
              <div className="text-xl font-bold font-mono">{s?.win_rate ?? 0}%</div>
              <div className="text-[10px] text-slate-600">{s?.wins ?? 0}W / {s?.losses ?? 0}L{s?.scratches ? ` / ${s.scratches} scratch` : ""}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Profit factor</div>
              <div className={`text-xl font-bold font-mono ${(s?.profit_factor ?? 0) >= 1 ? "text-up" : "text-down"}`}>
                {s?.profit_factor != null ? s.profit_factor.toFixed(2) : "—"}
              </div>
              <div className="text-[10px] text-slate-600">gross win ÷ gross loss</div>
            </div>
          </div>
        </div>
        {bookBreakdown && Object.keys(bookBreakdown).length > 1 && (
          <div className="text-xs text-slate-500 font-mono mt-2">
            {Object.entries(bookBreakdown).map(([b, v]) => (
              <span key={b} className="mr-3">
                {BOOK_LABEL[b] ?? b} <span className={v >= 0 ? "text-up" : "text-down"}>{usd(v)}</span>
              </span>
            ))}
          </div>
        )}
        {/* The bridge to the Firm page. These two numbers look like they
            disagree and don't: realized counts a closed trade's whole life,
            the day change counts only today's move on everything held. */}
        {data?.bridge && <DayBridge b={data.bridge} />}
        <EquityCurve curve={data?.equity_curve ?? []} />
      </div>

      {/* G-Pop's Read — the honest numbers, shown together on purpose so the
          flattering figure never appears without the one that qualifies it. */}
      {s && (s.arithmetic_avg_pnl_pct != null || s.real_return?.real_return_pct != null) && (
        <GPopRead stats={s} />
      )}

      {/* By sleeve — forex vs. crypto vs. options vs. equities, net of the
          fees and financing nothing else on this page shows. Own period
          control: "what's really happening" is a 30-day question more often
          than an all-time one. */}
      <SleevePerformance book={book} />

      {/* Capital at work — % of equity deployed over time and return on the
          dollars actually deployed vs. on total equity (Alan, 2026-08-19). */}
      <CapitalAtWork book={book} />

      {/* Secondary stats — still real, just not competing with the headline */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <Tile k="Expectancy" v={usd(s?.expectancy ?? 0)} sub="avg $ per trade" tone={(s?.expectancy ?? 0) >= 0 ? "up" : "down"} />
        <Tile k="Max drawdown" v={usd(s?.max_drawdown ?? 0)} sub="peak-to-trough" tone="down" />
        <Tile k="Avg win" v={usd(s?.avg_win ?? 0)} sub={`largest ${usd(s?.largest_win ?? 0)}`} tone="up" />
        <Tile k="Avg loss" v={usd(s?.avg_loss ?? 0)} sub={`largest ${usd(s?.largest_loss ?? 0)}`} tone="down" />
        <Tile k="Gross profit" v={usd(s?.gross_profit ?? 0)} tone="up" />
        <Tile k="Gross loss" v={usd(s?.gross_loss ?? 0)} tone="down" />
      </div>

      {/* Attribution — descriptive (junior/symbol) AND governance (board-
          directed vs mechanical), since those carry different risk profiles
          and were previously commingled with no visual split. */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* "(junior)" was only ever half true — the sleeves have no junior,
            and they are most of the book. The rows now name themselves. */}
        <AttrCard title="P&L by strategy" rows={data?.by_junior ?? []} />
        <AttrCard title="P&L by symbol" rows={data?.by_symbol ?? []} tickers />
        <AttrCard title="Board-directed vs. mechanical" rows={executionSplit} />
      </div>

      {/* Open positions — desktop table + mobile cards (the table used to be
          clipped invisible under the frame's overflow-x-hidden, Kaizen D3/F4;
          card pattern copied from Holdings). */}
      {(data?.open_positions?.length ?? 0) > 0 && (
        <div className="card">
          <h2 className="label">Open positions (unrealized, not yet in P&amp;L above)</h2>
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm font-mono">
              <thead><tr className="text-left text-xs text-slate-500 uppercase"><th className="py-1">Book</th><th>Symbol</th><th>Qty</th><th>Entry</th><th>Since</th></tr></thead>
              <tbody>
                {data!.open_positions.map((p, i) => (
                  <tr key={i} className="border-t border-edge">
                    <td className="py-1">{p.book}</td><td className="font-bold">{p.symbol}</td>
                    <td>{p.qty}</td><td>{px(p.symbol, p.entry_px)}</td>
                    <td className="text-slate-500 font-sans">{p.entry_time ? new Date(p.entry_time).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="sm:hidden space-y-2">
            {data!.open_positions.map((p, i) => (
              <div key={i} className="rounded-lg border border-edge p-2.5">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold">{p.symbol}</span>
                  <span className="text-xs text-slate-400">×{p.qty}</span>
                  <span className="badge bg-slate-800 text-slate-400 ml-auto">{p.book}</span>
                </div>
                <div className="text-xs font-mono text-slate-400 mt-1">
                  entry {px(p.symbol, p.entry_px)}
                  <span className="text-slate-600 font-sans"> · since {p.entry_time ? new Date(p.entry_time).toLocaleDateString() : "—"}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Traceable win/loss ledger */}
      <div className="card">
        <div className="flex items-baseline gap-2 flex-wrap">
          <h2 className="label mb-0">Closed trades — every win and loss, traceable</h2>
          <span className="text-xs text-accent">
            ▸ click a trade for the what-if (held longer? better exit?) · ▸ click "Story" for the full backward + forward record
          </span>
        </div>
        {/* Mobile: compact per-trade cards — the 11-column table pushed P&L
            off-screen at phone width with no way to scroll to it (Kaizen D3). */}
        <div className="sm:hidden space-y-2 mt-2">
          {(showOlder ? shown.all : shown.recent).map((t) => {
            const scratch = t.result === "scratch";
            return (
              <div key={t.trade_key} className="rounded-lg border border-edge p-2.5">
                <div className="flex items-center gap-2">
                  <span className={`badge text-[9px] ${t.side === "short" ? "bg-down/20 text-down" : "bg-up/15 text-up"}`}>
                    {t.side === "short" ? "SHORT" : "LONG"}
                  </span>
                  <Ticker symbol={t.symbol} className="font-mono font-bold" />
                  <span className={`ml-auto font-mono font-bold ${scratch ? "text-slate-500" : t.pnl >= 0 ? "text-up" : "text-down"}`}>
                    {scratch ? "scratch" : signedUsd(t.pnl)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-slate-500 font-mono mt-1">
                  <span>{t.exit_time ? new Date(t.exit_time).toLocaleString() : "—"}</span>
                  <span className="font-sans">{t.result ?? (t.win ? "win" : "loss")}</span>
                  <button className="btn text-[10px] ml-auto" onClick={() => setStoryId(t.trade_key)}>
                    Story →
                  </button>
                </div>
              </div>
            );
          })}
          {shown.all.length === 0 && (
            <div className="text-slate-600 text-sm py-2">no closed trades yet</div>
          )}
          {shown.all.length > 0 && shown.recent.length === 0 && !showOlder && (
            <div className="text-slate-600 text-sm py-2 font-sans">
              nothing closed in the last {RECENT_BUSINESS_DAYS} business days
            </div>
          )}
          <OlderToggle count={shown.older.length} open={showOlder}
                       onToggle={() => setShowOlder(!showOlder)} noun="older trades" />
        </div>
        <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 uppercase">
              <th className="py-1">Exit</th><th>Book</th><th>Symbol</th><th>Qty</th>
              <th>Entry→Exit</th><th>Hold</th><th>Trigger</th><th>Strategy</th><th>Cited</th><th className="text-right">P&amp;L</th><th></th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {(showOlder ? shown.all : shown.recent).map((t) => {
              const scratch = t.result === "scratch";
              const isOpen = openId === t.trade_key;
              return (
              // Keyed on trade_key (entry lot + exit order), which is unique per
              // closed lot. exit_order_id is NOT: one exit order can close
              // several FIFO-matched lots at once, so several visibly different
              // rows cite the same one. That collision caused two separate bugs
              // (found 2026-08-04): React couldn't tell rows apart across a
              // re-render, leaving stale rows from the previous book filter on
              // screen; and clicking any of those rows fetched the SAME what-if
              // and Story — whichever lot the backend matched first, not the one
              // clicked. trade_key now drives the key, the selection, and both
              // fetches, so each row resolves to its own record.
              <Fragment key={t.trade_key}>
              <tr onClick={() => toggleTrade(t.trade_key)}
                  className={`border-t border-edge cursor-pointer hover:bg-slate-800/40 ${scratch ? "" : t.win ? "" : "bg-down/5"} ${isOpen ? "bg-slate-800/50" : ""}`}>
                <td className="py-1.5 text-slate-500 whitespace-nowrap">
                  <span className={`inline-block mr-1.5 text-xs px-1 rounded ${isOpen ? "bg-accent/20 text-accent" : "bg-slate-800 text-accent"}`}>
                    {isOpen ? "▾ what-if" : "▸ what-if"}
                  </span>
                  {t.exit_time ? new Date(t.exit_time).toLocaleString() : "—"}
                </td>
                <td className={t.book === "live" ? "text-down" : "text-slate-400"}>{t.book}</td>
                <td className="font-bold">
                  {/* Direction is not decoration: without it "1.40330 -> 1.40630"
                      reads as a gain, and a SHORT that lost money looks like a
                      bug in the P&L (Alan, 2026-08-04: "how did we lose .0363 if
                      we entered lower than our exit??" — it was a short). */}
                  <span className={`badge mr-1.5 text-[9px] align-middle ${
                    t.side === "short" ? "bg-down/20 text-down" : "bg-up/15 text-up"}`}>
                    {t.side === "short" ? "SHORT" : "LONG"}
                  </span>
                  <Ticker symbol={t.symbol} />
                  {t.fx_unconverted && (
                    <span className="ml-1 badge bg-down/20 text-down text-[9px] align-middle" title="cross-currency pair — could not convert to USD, figure below is unconverted">⚠ FX</span>
                  )}
                  {t.fx_pair && !t.fx_unconverted && (
                    <span className="ml-1 text-slate-600 text-[9px] align-middle font-sans" title="converted to USD">fx</span>
                  )}
                </td>
                <td>{t.qty}</td>
                <td className="text-slate-400">{px(t.symbol, t.entry_px)}→{px(t.symbol, t.exit_px)}</td>
                <td className="text-slate-500">{t.hold_min != null ? fmtHold(t.hold_min) : "—"}</td>
                <td className="text-slate-500 font-sans">{t.exit_reason ?? "—"}</td>
                <td className="font-sans">
                  {t.junior ? (
                    <span className="text-slate-400">{t.junior}</span>
                  ) : (
                    <span className="badge bg-slate-800 text-slate-500 text-[10px]" title="crypto/forex sleeve — a standing rule fired, no board directive">mechanical</span>
                  )}
                </td>
                <td className="text-slate-500">{t.directives?.length ? `#${t.directives.join(",")}` : "—"}</td>
                <td className={`text-right font-bold ${scratch ? "text-slate-500" : t.pnl >= 0 ? "text-up" : "text-down"}`}>
                  {scratch ? "scratch" : usd(t.pnl)}{" "}
                  <span className="text-xs font-normal">({t.pnl_pct >= 0 ? "+" : ""}{t.pnl_pct}%)</span>
                </td>
                <td className="text-right">
                  <button className="btn text-xs"
                          onClick={(e) => { e.stopPropagation(); setStoryId(t.trade_key); }}>
                    Story →
                  </button>
                </td>
              </tr>
              {isOpen && (
                <tr><td colSpan={11} className="p-0">
                  <WhatIf detail={detail} trade={t} onOpenStory={() => setStoryId(t.trade_key)} />
                </td></tr>
              )}
              </Fragment>
              );
            })}
            {shown.all.length === 0 && (
              <tr><td colSpan={11} className="py-3 text-slate-600 font-sans">
                No closed trades yet — open positions close into wins/losses at their exit (target/stop) or the 15:50 ET flatten.
              </td></tr>
            )}
            {shown.all.length > 0 && shown.recent.length === 0 && !showOlder && (
              <tr><td colSpan={11} className="py-3 text-slate-600 font-sans">
                Nothing closed in the last {RECENT_BUSINESS_DAYS} business days.
              </td></tr>
            )}
            {shown.older.length > 0 && (
              <tr><td colSpan={11} className="border-t border-edge">
                <OlderToggle count={shown.older.length} open={showOlder}
                             onToggle={() => setShowOlder(!showOlder)} noun="older trades" />
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
      {storyId && <TradeStory exitOrderId={storyId} onClose={() => setStoryId(null)} />}
    </div>
  );
}

function TotalValueCard({ tv }: { tv: TotalValue }) {
  const up = tv.net_change >= 0;
  const W = 720, H = 90, pad = 6;
  const pts = tv.points.length > 1 ? (() => {
    const vs = tv.points.map((p) => p.value);
    const min = Math.min(...vs), max = Math.max(...vs), span = max - min || 1;
    const x = (i: number) => pad + (i / (tv.points.length - 1)) * (W - 2 * pad);
    const y = (v: number) => H - pad - ((v - min) / span) * (H - 2 * pad);
    return tv.points.map((p, i) => `${x(i)},${y(p.value)}`).join(" ");
  })() : null;
  return (
    <div className="card">
      <div className="flex items-end gap-6 flex-wrap">
        <div>
          <span className="label mb-0">Total value <span className="normal-case text-slate-500">— everything held, at current marks</span></span>
          <div className="text-4xl font-black font-mono leading-none mt-1 text-slate-100">{usd(tv.total_value)}</div>
          <span className="text-xs text-slate-500 font-mono">
            {Object.entries(tv.venues).map(([v, x]) => `${v} ${usd(x)}`).join(" · ")}
          </span>
        </div>
        <div className="flex gap-6">
          <div>
            <div className="text-xs text-slate-500">Earned <span className="text-slate-600">· {tv.days}d</span></div>
            <div className={`text-xl font-bold font-mono ${up ? "text-up" : "text-down"}`}>{signedUsd(tv.net_change)}</div>
            <div className="text-[10px] text-slate-600">
              {tv.funding !== 0 ? `${usd(tv.change)} change less ${usd(tv.funding)} you funded` : "no funding in the window"}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Time-weighted return</div>
            <div className={`text-xl font-bold font-mono ${tv.twr_pct >= 0 ? "text-up" : "text-down"}`}>
              {tv.twr_pct >= 0 ? "+" : ""}{tv.twr_pct.toFixed(2)}%
            </div>
            <div className="text-[10px] text-slate-600">neutral to when money was added</div>
          </div>
        </div>
      </div>
      {pts && (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full mt-2" style={{ maxHeight: 100 }}>
          <polyline points={pts} fill="none" stroke={up ? "#2ec4b6" : "#ff3b4d"} strokeWidth={2}
                    strokeLinejoin="round" strokeLinecap="round" />
        </svg>
      )}
      <p className="text-[10px] text-slate-600 mt-1">
        {tv.note}
        {tv.partial_days > 0 && ` · ${tv.partial_days} day(s) missing a venue read — those points are a lower bound.`}
      </p>
    </div>
  );
}

function DayBridge({ b }: { b: Bridge }) {
  const Cell = ({ k, v, tone }: { k: string; v: number; tone?: boolean }) => (
    <div className="text-center">
      <div className="text-[9px] uppercase tracking-wide text-slate-500">{k}</div>
      <div className={`font-mono text-sm font-bold ${tone === undefined ? "text-slate-200" : v >= 0 ? "text-up" : "text-down"}`}>
        {signedUsd(v)}
      </div>
    </div>
  );
  return (
    <div className="mt-3 rounded border border-slate-800 px-3 py-2">
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <Cell k="realized today" v={b.realized_today} tone />
        <span className="text-slate-600 font-mono">+</span>
        <Cell k="open positions moved" v={b.unrealized_move} tone />
        {Math.abs(b.day_funding) >= 0.005 && (
          <>
            <span className="text-slate-600 font-mono">+</span>
            <Cell k="funding (not earnings)" v={b.day_funding} />
          </>
        )}
        <span className="text-slate-600 font-mono">=</span>
        <div className="text-center">
          <div className="text-[9px] uppercase tracking-wide text-slate-500">Firm page day change</div>
          <div className={`font-mono text-sm font-bold ${b.day_change >= 0 ? "text-up" : "text-down"}`}>
            {signedUsd(b.day_change)}
          </div>
        </div>
      </div>
      <p className="text-[10px] text-slate-600 text-center mt-1 leading-snug">{b.note}</p>
    </div>
  );
}

function EquityCurve({ curve }: { curve: Curve[] }) {
  const W = 720, H = 160, pad = 8;
  const pts = useMemo(() => {
    if (curve.length === 0) return null;
    const ys = [0, ...curve.map((c) => c.cum)];
    const min = Math.min(...ys), max = Math.max(...ys), span = max - min || 1;
    const n = curve.length;
    const x = (i: number) => pad + (n <= 1 ? 0 : (i / (n - 1)) * (W - 2 * pad));
    const y = (v: number) => H - pad - ((v - min) / span) * (H - 2 * pad);
    const line = curve.map((c, i) => `${x(i)},${y(c.cum)}`).join(" ");
    const zeroY = y(0);
    const last = curve[curve.length - 1].cum;
    return { line, zeroY, last, first: { x: x(0), y: y(curve[0].cum) }, lastPt: { x: x(n - 1), y: y(last) } };
  }, [curve]);

  if (!pts) return <div className="text-slate-600 text-sm py-8 text-center font-sans">Equity curve appears once the first trade closes.</div>;
  const up = pts.last >= 0;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full mt-2" style={{ maxHeight: 180 }}>
      <line x1={pad} y1={pts.zeroY} x2={W - pad} y2={pts.zeroY} stroke="#334155" strokeDasharray="3 3" strokeWidth={1} />
      <polyline points={pts.line} fill="none" stroke={up ? "#2ec4b6" : "#ff3b4d"} strokeWidth={2} />
      <circle cx={pts.lastPt.x} cy={pts.lastPt.y} r={3} fill={up ? "#2ec4b6" : "#ff3b4d"} />
    </svg>
  );
}

function ReconBadge({ recon }: { recon: ReconStatus | null }) {
  // A live external confidence signal — everything else on this page is
  // self-reported. Added 2026-08-04 (CFO review): today's real P&L bug was
  // arithmetic internal to performance.py; reconciliation checks broker
  // POSITIONS, not P&L math, so this badge doesn't guarantee the numbers are
  // right — but it does guarantee the ledger isn't drifting from reality,
  // which is the check a CFO would actually ask for first.
  if (!recon) return null;
  if (recon.ok === null) {
    return <span className="badge bg-slate-800 text-slate-500 text-[10px]" title={recon.error}>reconciliation unavailable</span>;
  }
  return recon.ok ? (
    <span className="badge bg-up/20 text-up text-[10px]">ledger ✓ ties out</span>
  ) : (
    <span className="badge bg-down/20 text-down text-[10px]">
      ⚠ {recon.critical_count} discrepanc{recon.critical_count === 1 ? "y" : "ies"} — see Control Panel
    </span>
  );
}

// The honest-numbers callout: arithmetic vs. geometric average return (the
// gap IS the cost of volatility, not a rounding difference) and nominal vs.
// real return on capital actually deployed. Always shown paired — one
// number alone is exactly the "lipstick on a pig" this exists to strip off.
function GPopRead({ stats: s }: { stats: Stats }) {
  const rr = s.real_return;
  return (
    <div className="card" style={{ background: "rgba(120,140,100,0.08)", borderColor: "rgba(120,140,100,0.35)" }}>
      <div className="flex items-center justify-between">
        <span className="label mb-0">G-Pop&rsquo;s Read — the honest numbers</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-1">
        {s.arithmetic_avg_pnl_pct != null && (
          <div>
            <div className="text-xs text-slate-500">Per-trade return: simple avg vs. compounded (geometric)</div>
            <div className="text-base font-bold font-mono">
              {pct(s.arithmetic_avg_pnl_pct)} <span className="text-slate-500 font-normal">simple</span>
              {" · "}
              {pct(s.geometric_avg_pnl_pct)} <span className="text-slate-500 font-normal">compounded</span>
            </div>
            {s.return_gap_pct != null && s.return_gap_pct > 0.01 && (
              <div className="text-[10px] text-slate-600">{s.return_gap_pct.toFixed(2)}pt gap — the cost of volatility, not a rounding error</div>
            )}
          </div>
        )}
        {rr?.real_return_pct != null && (
          <div>
            <div className="text-xs text-slate-500">Return on capital deployed, {rr.period_days}d: nominal vs. real</div>
            <div className="text-base font-bold font-mono">
              {pct(rr.nominal_return_pct)} <span className="text-slate-500 font-normal">nominal</span>
              {" · "}
              <span className={(rr.real_return_pct ?? 0) >= 0 ? "text-up" : "text-down"}>{pct(rr.real_return_pct)}</span>
              <span className="text-slate-500 font-normal"> real</span>
            </div>
            <div className="text-[10px] text-slate-600">after {rr.assumed_inflation_pct}%/yr assumed inflation, not annualized</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ k, v, sub, tone }: { k: string; v: string; sub?: string; tone?: "up" | "down" }) {
  return (
    <div className="card py-2">
      <div className="text-xs text-slate-500">{k}</div>
      <div className={`text-lg font-bold font-mono ${tone === "up" ? "text-up" : tone === "down" ? "text-down" : "text-slate-200"}`}>{v}</div>
      {sub && <div className="text-[10px] text-slate-600">{sub}</div>}
    </div>
  );
}

function AttrCard({ title, rows, tickers = false }:
                  { title: string; rows: Attr[]; tickers?: boolean }) {
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.pnl)));
  return (
    <div className="card">
      <h2 className="label">{title}</h2>
      {rows.length === 0 && <div className="text-slate-600 text-sm">nothing closed yet</div>}
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.k} className="flex items-center gap-2 text-xs">
            {/* w-32, not w-28: the sleeve names this card now carries
                ("commodities sleeve") clipped at the old width. title= keeps
                the full label reachable if it still truncates on a phone. */}
            <span className="w-32 shrink-0 truncate text-slate-300" title={tickers ? undefined : r.k}>
              {tickers ? <Ticker symbol={r.k} /> : r.k}
            </span>
            <div className="flex-1 h-3 bg-slate-800 rounded overflow-hidden flex">
              <div className={`h-full ${r.pnl >= 0 ? "bg-up/60" : "bg-down/60"}`} style={{ width: `${(Math.abs(r.pnl) / max) * 100}%` }} />
            </div>
            <span className={`w-16 text-right font-mono ${r.pnl >= 0 ? "text-up" : "text-down"}`}>{usd(r.pnl)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function WhatIf({ detail, trade, onOpenStory }: { detail: TradeDetail | null; trade: Trade; onOpenStory: () => void }) {
  if (!detail) return <div className="p-4 text-xs text-slate-500 bg-ink/40 font-sans">loading what-if…</div>;
  const fin = detail.if_held.length ? detail.if_held[detail.if_held.length - 1] : null;
  const closed = fin?.final === true;
  const soFar = closed ? "" : " so far — the market's still open";
  const verdict = (() => {
    if (fin && fin.vs_actual > 0.005)
      return { t: closed
        ? `Exited early — holding to close would have been ${usd(fin.vs_actual)} better.`
        : `So far, holding would have been ${usd(fin.vs_actual)} better — but the day isn't done.`, c: "text-gold" };
    if (fin && fin.vs_actual < -0.005)
      return { t: `Clean exit — it's fallen ${usd(Math.abs(fin.vs_actual))} further since we sold${soFar}.`, c: "text-up" };
    if (detail.best_exit && detail.best_exit.left_on_table > 0.005)
      return { t: `Left ${usd(detail.best_exit.left_on_table)} on the table vs the day's best${soFar ? " (so far)" : ""}.`, c: "text-gold" };
    return { t: `Exited at/near the best price available${soFar}.`, c: "text-up" };
  })();
  return (
    <div className="p-4 bg-ink/40 space-y-3 font-sans border-l-2 border-accent/40">
      <div className="flex items-start justify-between gap-3">
        <div className={`text-sm font-semibold ${verdict.c}`}>{verdict.t}</div>
        <button className="btn text-xs shrink-0" onClick={onOpenStory}>Full story →</button>
      </div>
      <PricePath path={detail.path} entryPx={trade.entry_px} exitPx={trade.exit_px}
                 entryTime={trade.entry_time} exitTime={trade.exit_time} />
      <div className="grid md:grid-cols-2 gap-5 text-xs">
        <div>
          <div className="label">What if we&rsquo;d held past the exit?</div>
          {detail.if_held.length === 0 && <div className="text-slate-600">no post-exit data</div>}
          <table className="w-full font-mono">
            <tbody>
              {detail.if_held.map((h, i) => (
                <tr key={i} className="border-t border-edge">
                  <td className="py-0.5 text-slate-500 w-16">{h.label}</td>
                  <td className="text-slate-400">{px(trade.symbol, h.price)}</td>
                  <td className={`text-right ${h.vs_actual >= 0 ? "text-up" : "text-down"}`}>
                    {h.vs_actual >= 0 ? "+" : ""}{usd(h.vs_actual)} <span className="text-slate-600">vs our exit</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="space-y-3">
          {detail.best_exit && (
            <div>
              <div className="label">Best exit available{closed ? "" : " (so far today)"}</div>
              <div className="font-mono text-slate-300">
                {px(trade.symbol, detail.best_exit.price)} at {fmtTime(detail.best_exit.t)}
              </div>
              <div className={`${detail.best_exit.left_on_table > 0.005 ? "text-gold" : "text-slate-500"}`}>
                {detail.best_exit.left_on_table > 0.005
                  ? `left ${usd(detail.best_exit.left_on_table)} on the table`
                  : "we exited at/near the peak"}
              </div>
            </div>
          )}
          <div>
            <div className="label">Were our signals fresh at entry?</div>
            {Object.keys(detail.signal_freshness).length === 0 && <div className="text-slate-600">no signal record</div>}
            {Object.entries(detail.signal_freshness).map(([k, v]) => (
              <div key={k} className="text-slate-400">
                {k} read refreshed{" "}
                <span className={v.min_before_entry > 45 ? "text-gold" : "text-up"}>{v.min_before_entry}m</span>{" "}
                before entry {v.min_before_entry > 45 ? "(stale)" : "(fresh)"}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function PricePath({ path, entryPx, exitPx, entryTime, exitTime }: {
  path: { t: string; c: number }[]; entryPx: number; exitPx: number;
  entryTime: string | null; exitTime: string | null;
}) {
  const W = 680, H = 120, pad = 10;
  if (path.length < 2) return <div className="text-xs text-slate-600 font-mono">no minute-bar path available for this trade</div>;
  const cs = path.map((p) => p.c);
  const min = Math.min(...cs, entryPx, exitPx), max = Math.max(...cs, entryPx, exitPx), span = max - min || 1;
  const n = path.length;
  const x = (i: number) => pad + (i / (n - 1)) * (W - 2 * pad);
  const y = (v: number) => H - pad - ((v - min) / span) * (H - 2 * pad);
  const idxAt = (t: string | null) => {
    if (!t) return 0;
    const target = new Date(t).getTime();
    let best = 0, bd = Infinity;
    path.forEach((p, i) => { const d = Math.abs(new Date(p.t).getTime() - target); if (d < bd) { bd = d; best = i; } });
    return best;
  };
  const ei = idxAt(entryTime), xi = idxAt(exitTime);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 130 }}>
      <rect x={x(xi)} y={pad} width={W - pad - x(xi)} height={H - 2 * pad} fill="#8b93a7" opacity={0.06} />
      <polyline points={path.map((p, i) => `${x(i)},${y(p.c)}`).join(" ")} fill="none" stroke="#3aa0ff" strokeWidth={1.5} />
      <line x1={x(ei)} y1={pad} x2={x(ei)} y2={H - pad} stroke="#2ec4b6" strokeDasharray="3 3" strokeWidth={1} opacity={0.6} />
      <line x1={x(xi)} y1={pad} x2={x(xi)} y2={H - pad} stroke="#ff3b4d" strokeDasharray="3 3" strokeWidth={1} opacity={0.6} />
      <circle cx={x(ei)} cy={y(entryPx)} r={3.5} fill="#2ec4b6" />
      <circle cx={x(xi)} cy={y(exitPx)} r={3.5} fill="#ff3b4d" />
      <text x={x(ei) + 4} y={y(entryPx) - 6} fontSize="9" fill="#2ec4b6" className="font-mono">entry</text>
      <text x={x(xi) + 4} y={y(exitPx) - 6} fontSize="9" fill="#ff3b4d" className="font-mono">exit</text>
      <text x={W - pad - 2} y={pad + 10} fontSize="8" fill="#8b93a7" textAnchor="end" className="font-mono">what happened after →</text>
    </svg>
  );
}

export function fmtHold(min: number): string {
  if (min < 60) return `${min}m`;
  if (min < 1440) return `${Math.round(min / 60)}h`;
  return `${Math.round(min / 1440)}d`;
}
